import React, { useContext, useEffect, useState } from "react";
import { ThreeRow, TwoRow } from "../../core/layouts";
import CustomSelect from "../../components/CustomSelect/CustomSelect";
import { getLocalizedMessage } from "../../UtilityFunctions/utils";
import { useIntl } from "react-intl";
import { Text, Space, Button } from "react_reusable_components";
import Style from "./PlanAuditContainer.module.scss";
import PlanAuditContainerLayout from "../../layouts/PlanAuditContainerLayout";
import variables from "../../themes/base/styles/variables";
import PlanRepairOperationAuditContainer from "../PlanRepairOperationAuditContainer";
import { useFormik } from "formik";
import {
  planAuditInitialValues,
  planAuditValidationSchema,
  planAuditFieldName,
} from "../../validationSchema/planAuditSchema";
import { userRole } from "../../constants";
import AuditService from "../../services/audit-service";
import moment from "moment";
import { GlobalLoaderContext } from "../../globalContext/globalLoader/globalLoaderProvider";
import ConfirmModalContainer from "../ConfirmModalContainer/ConfirmModalContainer";
import {
  NO_QUESTIONNAIRE,
  OPERATIONAL_PRIMARY,
} from "../../constants/errorConstants";
import { useNavigate } from "react-router-dom";

const PlanAuditContainerStyle = {
  topSectionStyle: { flex: 1 },
  bottomSectionStyle: {
    marginBottom: variables.marginMedium,
    display: variables.dFlex,
    justifyContent: "end",
    bottom: variables.paddingMedium,
    right: variables.paddingMedium,
    background: variables.bodyBg,
    paddingRight: variables.paddingMedium,
  },
  tmSectionStyle: { marginTop: variables.marginLarge },
  auditSectionStyle: { marginTop: variables.marginLarge },
};

export default function PlanAuditContainer() {
  const [disabled, setDisabled] = useState();
  const intl = useIntl();
  const navigate = useNavigate();
  const loaderDispatch = useContext(GlobalLoaderContext)[1];
  const DATE_FORMAT = intl.formatMessage({ id: "date.format" });

  const [confirmModal, setConfirmModal] = useState(false);
  const [noPrimaryQuestionnaire, setNoPrimaryQuestionnaire] = useState(false);

  const [productSubProductList, setProductSubProductList] = useState([]);
  const [tmList, setTmList] = useState([]);
  const [dnsdList, setDnsdList] = useState([]);

  const {
    values,
    handleChange,
    errors,
    touched,
    handleSubmit,
    setFieldValue,
    setFieldError,
    resetForm,
    setValues,
    validateField,
    setFieldTouched,
    setErrors,
  } = useFormik({
    initialValues: planAuditInitialValues,
    validationSchema: planAuditValidationSchema(intl),
    onSubmit: async (data) => {
      planAudit();
    },
  });

  function validateTentativeDates(data = values) {
    return new Promise((resolve) => {
      setTimeout(() => {
        let flag = true;
        data[planAuditFieldName.DNSD].forEach((dnsd, i) => {
          if (!data[planAuditFieldName.TENTATIVE_DATE][i]) {
            flag = false;
            setFieldError(
              planAuditFieldName.TENTATIVE_DATE + `.${i}`,
              intl.formatMessage({
                id: "planAudit.fieldError.tentativeDate.required",
              })
            );
          }
        });

        resolve(flag);
      }, 100);
    });
  }

  function getSubCategoriesList(productCategory = []) {
    const subCategories = [];
    productCategory?.forEach(({ subCategory }) => {
      subCategory?.forEach((category) => {
        subCategories.push(category);
      });
    });
    return subCategories;
  }

  useEffect(() => {
    if (!values[planAuditFieldName.AUDIT_TYPE]) return;
    AuditService.getProductSubProductList({ intl, loaderDispatch })
      .then((data) => {
        setProductSubProductList(getSubCategoriesList(data));
      })
      .catch(() => {}); //NOSONAR
    AuditService.getUsersList({
      loaderDispatch,
      intl,
      params: {
        ...(values[planAuditFieldName.PRODUCT_SUB_CATEGORY_ID] && {
          subProductCategoryId:
            values[planAuditFieldName.PRODUCT_SUB_CATEGORY_ID] || "",
        }),
        roleId: userRole.DNSD,
      },
    })
      .then((data) => {
        setDnsdList(data);
      })
      .catch(() => {}); //NOSONAR
    AuditService.getUsersList({
      loaderDispatch,
      intl,
      params: {
        ...(values[planAuditFieldName.PRODUCT_SUB_CATEGORY_ID] && {
          subProductCategoryId:
            values[planAuditFieldName.PRODUCT_SUB_CATEGORY_ID] || "",
        }),
        roleId: userRole.TM,
      },
    })
      .then((data) => {
        setTmList(data);
      })
      .catch(() => {}); //NOSONAR
  }, [
    values[planAuditFieldName.AUDIT_TYPE],
    values[planAuditFieldName.PRODUCT_SUB_CATEGORY_ID],
  ]);

  async function planAudit() {
    setDisabled(true);
    const body = {
      ...values,
      dnsd: values[planAuditFieldName.DNSD_OBJ_ARR].map(
        ({ dnsd_id, tentative_date, tm_id }) => ({
          dnsd_id,
          tentative_date: moment(tentative_date, DATE_FORMAT).format(
            "YYYY-MM-DD HH:mm:ss.SSSSSSZ"
          ),
          tm_id,
        })
      ),
    };

    if (body[planAuditFieldName.AUDIT_TYPE] === "Operational")
      delete body.product_sub_category_id;
    delete body[planAuditFieldName.DNSD_OBJ_ARR];

    AuditService.planAudit({
      intl,
      body,
      loaderDispatch,
      callback: (code) => {
        code === NO_QUESTIONNAIRE
          ? setConfirmModal(true)
          : code === OPERATIONAL_PRIMARY && setNoPrimaryQuestionnaire(true);
      },
    })
      .then((data) => {
        setDisabled(false);
        navigate("/audit-listing");
      })
      .catch((err) => {
        setDisabled(false);
      }); 
  }

  function onCancel() {
    resetForm();
    navigate(-1);
  }

  return (
    <>
      <TwoRow
        topSectionStyle={PlanAuditContainerStyle.topSectionStyle}
        topSection={
          <ThreeRow
            style={{ padding: variables.paddingMedium }}
            topSection={
              <PlanAuditContainerLayout
                leftSection={
                  <CustomSelect
                    label={getLocalizedMessage(
                      intl,
                      "planAudit.selectAuditType.label"
                    )}
                    required={true}
                    placeholder={getLocalizedMessage(
                      intl,
                      "planAudit.selectAuditType.placeholder"
                    )}
                    showSearch={false}
                    options={[
                      { name: "Operational", value: "Operational" },
                      { name: "Repair", value: "Repair" },
                    ]?.map(({ name, value }) => {
                      return {
                        label: (
                          <Text className={`${Style["select-item-style"]}`}>
                            {name}
                          </Text>
                        ),
                        value: value,
                      };
                    })}
                    name={planAuditFieldName.AUDIT_TYPE}
                    value={values[planAuditFieldName.AUDIT_TYPE]}
                    onChange={(e) => {
                      setValues((prev) => ({
                        ...prev,
                        [planAuditFieldName.AUDIT_TYPE]: e,
                        [planAuditFieldName.DNSD]: [],
                        [planAuditFieldName.PRODUCT_SUB_CATEGORY_ID]: "",
                      }));
                      setErrors((prev) => ({
                        ...prev,
                        [planAuditFieldName.DNSD]: "",
                        [planAuditFieldName.PRODUCT_SUB_CATEGORY_ID]: "",
                      }));
                    }}
                    onBlur={() => {
                      setFieldTouched(planAuditFieldName.AUDIT_TYPE, true);
                      validateField(planAuditFieldName.AUDIT_TYPE);
                    }}
                    error={
                      touched[planAuditFieldName.AUDIT_TYPE] &&
                      errors[planAuditFieldName.AUDIT_TYPE]
                    }
                  />
                }
              />
            }
            middleSectionStyle={PlanAuditContainerStyle.auditSectionStyle}
            middleSection={
              values[planAuditFieldName.AUDIT_TYPE] && (
                <PlanRepairOperationAuditContainer
                  dnsdList={dnsdList}
                  tmList={tmList}
                  productSubProductList={productSubProductList}
                  values={values}
                  handleChange={handleChange}
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                  validateField={validateField}
                  validateTentativeDates={validateTentativeDates}
                  setValues={setValues}
                />
              )
            }
            bottomSectionStyle={PlanAuditContainerStyle.tmSectionStyle}
          />
        }
        bottomSectionStyle={PlanAuditContainerStyle.bottomSectionStyle}
        bottomSection={
          <Space size={0}>
            <Button
              className={Style["cancel-btn"]}
              ghost
              icon={null}
              onClick={onCancel}
            >
              {getLocalizedMessage(intl, "button.text.cancel")}
            </Button>
            <Button
              className={Style["Add-Product-Category"]}
              icon={null}
              disabled={disabled}
              onClick={handleSubmit}
            >
              {getLocalizedMessage(intl, "button.save")}
            </Button>
          </Space>
        }
      />
      <ConfirmModalContainer
        iconSrc={"/icons/warningIcon.svg"}
        question={getLocalizedMessage(intl, "planAudit.noQuestionnairePrimary")}
        descriptionText={getLocalizedMessage(
          intl,
          "planAudit.noQuestionnairePrimary.desc"
        )}
        isCancelBtn={false}
        open={noPrimaryQuestionnaire}
        restStyle={{ backgroundColor: variables.primaryBg }}
        confirmText={getLocalizedMessage(intl, "text.okay")}
        setOpen={setNoPrimaryQuestionnaire}
      />
      <ConfirmModalContainer
        iconSrc={"/icons/warningIcon.svg"}
        question={getLocalizedMessage(
          intl,
          "planAudit.noQuestionnaireAssigned"
        )}
        descriptionText={getLocalizedMessage(
          intl,
          "planAudit.noQuestionnaireAssigned.desc"
        )}
        isCancelBtn={false}
        setOpen={setConfirmModal}
        open={confirmModal}
        restStyle={{ backgroundColor: variables.primaryBg }}
        confirmText={getLocalizedMessage(intl, "text.okay")}
      />
    </>
  );
}
